import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import posthog from "posthog-js"
import { useCallback, useRef, useState } from "react"
import {
	clearVerificationCooldown,
	isTrialEligible,
	type SubscriptionStatus,
	subscriptionStatusQueryOptions,
	useSubscriptionStatus,
} from "@/entities/subscription"
import { revalidateUser, useCurrentUser } from "@/entities/user"
import * as m from "@/paraglide/messages"
import { captureError } from "@/shared/lib/diagnostics"
import {
	type Platform,
	type StoreId,
	usePlatform,
	useStoreId,
} from "@/shared/lib/platform"
import { showSystemPopup } from "@/shared/lib/platform/popup"
import { type PlanId, parsePlanId } from "../model/plans"
import type {
	ProductInfo,
	PurchaseAttempt,
	PurchaseInterface,
	PurchaseOptions,
	PurchaseProvider,
	PurchaseResult,
	RestoreResult,
} from "../model/types"
import {
	isPaymentPending,
	isPurchaseUnavailable,
	isUserCanceled,
	type PurchaseUnavailableReason,
	purchaseErrorProps,
} from "./errors"
import {
	getProducts as getRuStoreProducts,
	purchaseProduct as purchaseRuStoreProduct,
	restoreTransactions as restoreRuStoreTransactions,
} from "./platforms/rustore"
import {
	buildWebPaymentUrl,
	clearPendingWebPayment,
	getPendingWebPayment,
	openWebPaymentUrl,
	setPendingWebPayment,
} from "./web-payment"

// CloudPayments activates through a webhook — poll /status until it lands.
// Total window ≈ POLL_INTERVAL_MS * MAX_POLL_ATTEMPTS.
const POLL_INTERVAL_MS = 1000
const MAX_POLL_ATTEMPTS = 6

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

function unavailableMessage(reason: PurchaseUnavailableReason): string {
	switch (reason) {
		case "app-banned":
			return m.subscriptionAlertPurchaseUnavailableAppBanned()
		case "user-banned":
			return m.subscriptionAlertPurchaseUnavailableUserBanned()
		case "store-outdated":
			return m.subscriptionAlertPurchaseUnavailableStoreOutdated()
		case "billing-unavailable":
			return m.subscriptionAlertPurchaseUnavailableBillingUnavailable()
		case "unknown":
			return m.subscriptionAlertPurchaseUnavailableMessage()
	}
}

function resolveProvider(
	store: StoreId | null,
	platform: Platform,
): PurchaseProvider | null {
	switch (store) {
		case "google-play":
			return "google-play"
		case "app-store":
			return "app-store"
		case "rustore":
			return "rustore"
		case null:
			if (platform === "telegram") return "cloud-payments"
			return null
	}
}

function resolveRussianStore(
	provider: PurchaseProvider | null,
	products: ProductInfo[],
): boolean {
	if (provider !== "app-store" && provider !== "google-play") return false
	if (products.length === 0) return false
	return products.every((p) => p.currencyCode === "RUB")
}

const loadPlatformModule = {
	"cloud-payments": () => import("./platforms/cloud-payments"),
	"google-play": () => import("./platforms/google-play"),
	"app-store": () => import("./platforms/app-store"),
	// Already in the entry chunk: boot runs migrateLegacyPendingPurchases
	// synchronously, so a dynamic import would split nothing.
	rustore: async () => ({
		getProducts: getRuStoreProducts,
		purchaseProduct: purchaseRuStoreProduct,
		restoreTransactions: restoreRuStoreTransactions,
	}),
} as const

const purchaseKeys = {
	all: ["purchase"] as const,
	products: (provider: PurchaseProvider | null) =>
		[...purchaseKeys.all, "products", provider] as const,
}

export function productsQueryOptions(provider: PurchaseProvider | null) {
	return queryOptions({
		queryKey: purchaseKeys.products(provider),
		queryFn: async (): Promise<ProductInfo[]> => {
			if (!provider) return []
			const mod = await loadPlatformModule[provider]()
			return mod.getProducts()
		},
		staleTime: Number.POSITIVE_INFINITY,
		gcTime: Number.POSITIVE_INFINITY,
		retry: 1,
		meta: {
			onError: (error) => {
				console.error("[usePurchase] getProducts failed:", error)
			},
		},
	})
}

/**
 * Prewarm store prices so the promo opens with real prices already in hand.
 * Mounted once at app start (in the always-present promo dialog), it kicks off
 * the slow native StoreKit / Play Billing lookup in the background — by the time
 * the user taps any "monster" entry point, `usePurchase` reads the cached
 * products synchronously instead of flashing the empty/"free" state first.
 * `enabled` lets subscribed users skip it (they see the benefits screen).
 */
export function usePrefetchProducts(enabled = true): void {
	const platform = usePlatform()
	const store = useStoreId()
	const provider = resolveProvider(store, platform)
	useQuery({
		...productsQueryOptions(provider),
		enabled: enabled && provider !== null,
	})
}

export function usePurchase(): PurchaseInterface {
	const platform = usePlatform()
	const store = useStoreId()
	const queryClient = useQueryClient()
	const router = useRouter()
	const user = useCurrentUser()
	const { data: status } = useSubscriptionStatus()

	const [isLoading, setIsLoading] = useState(false)
	// React state lags one render behind, so quick double-taps slip past
	// `disabled={isLoading}`. Mirror the flag in a ref for synchronous guarding.
	const isInProgressRef = useRef(false)

	const provider = resolveProvider(store, platform)

	// Read from the shared cache that the prewarm (`usePrefetchProducts`) fills at
	// app start, so opening the promo shows real prices immediately.
	const productsQuery = useQuery({
		...productsQueryOptions(provider),
		enabled: provider !== null,
	})
	const products = productsQuery.data ?? []
	const productsError = productsQuery.isError

	const isRussianStore = resolveRussianStore(provider, products)
	const trialEligible = isTrialEligible(status) && !isRussianStore

	const fetchStatus =
		useCallback(async (): Promise<SubscriptionStatus | null> => {
			try {
				return await queryClient.query({
					...subscriptionStatusQueryOptions(),
					staleTime: 0,
				})
			} catch {
				return null
			}
		}, [queryClient])

	const refreshSubscription = useCallback(async () => {
		const status = await fetchStatus()
		await revalidateUser(queryClient)
		await router.invalidate({ sync: true })
		return status
	}, [fetchStatus, queryClient, router])

	const syncAfterPurchase = useCallback(
		async (seed?: SubscriptionStatus): Promise<boolean> => {
			if (seed) {
				const { queryKey } = subscriptionStatusQueryOptions()
				queryClient.setQueryData(queryKey, seed)
				const status = await refreshSubscription()
				return status?.active ?? false
			}
			let status: SubscriptionStatus | null = null
			for (let attempt = 1; attempt <= MAX_POLL_ATTEMPTS; attempt++) {
				await sleep(POLL_INTERVAL_MS)
				status = await fetchStatus()
				if (status?.active) break
			}
			if (!status?.active) {
				console.warn(
					`[usePurchase] subscription not active after ${MAX_POLL_ATTEMPTS} polls — webhook likely delayed`,
				)
			}
			await revalidateUser(queryClient)
			await router.invalidate({ sync: true })
			return status?.active ?? false
		},
		[queryClient, fetchStatus, refreshSubscription, router],
	)

	const runPurchase = useCallback(
		async (
			planId: PlanId,
			trial: boolean,
			operation: () => Promise<PurchaseAttempt>,
		): Promise<PurchaseResult> => {
			if (isInProgressRef.current) return { processed: false, entitled: false }
			isInProgressRef.current = true
			setIsLoading(true)
			clearVerificationCooldown()
			try {
				const attempt = await operation()
				if (!attempt.processed) return { ...attempt, entitled: false }

				const entitled = await syncAfterPurchase(attempt.subscription)
				if (!entitled) {
					void showSystemPopup({
						title: m.subscriptionAlertPendingActivationTitle(),
						message: m.subscriptionAlertPendingActivationMessage(),
						buttons: [
							{ id: "ok", text: m.subscriptionResultBtnOk(), type: "ok" },
						],
					})
				}
				return { ...attempt, entitled }
			} catch (error) {
				console.error("Purchase failed:", error)
				if (isPaymentPending(error)) {
					void showSystemPopup({
						title: m.subscriptionAlertPaymentPendingTitle(),
						message: m.subscriptionAlertPaymentPendingMessage(),
						buttons: [
							{ id: "ok", text: m.subscriptionResultBtnOk(), type: "ok" },
						],
					})
					return { processed: false, entitled: false }
				}
				if (isPurchaseUnavailable(error)) {
					posthog.capture("purchase_unavailable", {
						plan_id: planId,
						reason: error.reason,
					})
					void showSystemPopup({
						title: m.subscriptionAlertPurchaseUnavailableTitle(),
						message: unavailableMessage(error.reason),
						buttons: [
							{ id: "ok", text: m.subscriptionResultBtnOk(), type: "ok" },
						],
					})
					return { processed: false, entitled: false }
				}
				if (!isUserCanceled(error)) {
					captureError(error, {
						scope: "purchase.flow",
						plan_id: planId,
						trial,
						...purchaseErrorProps(error),
					})
					void showSystemPopup({
						title: m.subscriptionAlertPurchaseFailedTitle(),
						message: m.subscriptionAlertPurchaseFailedMessage(),
						buttons: [
							{ id: "ok", text: m.subscriptionResultBtnOk(), type: "ok" },
						],
					})
				}
				return { processed: false, entitled: false }
			} finally {
				setIsLoading(false)
				isInProgressRef.current = false
			}
		},
		[syncAfterPurchase],
	)

	const purchase = useCallback(
		async (
			planId: PlanId,
			options?: PurchaseOptions,
		): Promise<PurchaseResult> => {
			// The feature owns the trial policy: default to the backend gate, and
			// treat `options.trial` only as an explicit override. Callers don't
			// thread eligibility back in. Refuse a trial the backend hasn't
			// granted — `trialEligible` is false until /status loads, so this also
			// guards the not-yet-loaded case.
			const trial = options?.trial ?? trialEligible
			if (trial && !trialEligible) {
				console.warn("[usePurchase] trial requested but user is not eligible")
				return { processed: false, entitled: false }
			}

			if (provider === "cloud-payments") {
				if (!user.tg_user_id) return { processed: false, entitled: false }
				return runPurchase(planId, trial, async () => {
					const mod = await loadPlatformModule["cloud-payments"]()
					return mod.purchaseProduct(user.tg_user_id as string, planId, {
						trial,
						email: user.email,
					})
				})
			}

			// Google Play uses developer-determined offer eligibility, so we pass
			// the trial flag to pick the trial offer vs the plain paid base plan.
			if (provider === "google-play") {
				return runPurchase(planId, trial, async () => {
					const mod = await loadPlatformModule["google-play"]()
					return mod.purchaseProduct(planId, { trial })
				})
			}

			// App Store applies its introductory offer automatically (Apple-side
			// eligibility), so the trial flag changes nothing in the purchase call.
			if (provider === "app-store") {
				return runPurchase(planId, trial, async () => {
					const mod = await loadPlatformModule["app-store"]()
					return mod.purchaseProduct(planId)
				})
			}

			// RuStore owns free-period eligibility the same way Apple does, so the
			// trial flag changes nothing in the purchase call.
			if (provider === "rustore") {
				return runPurchase(planId, trial, async () => {
					const mod = await loadPlatformModule.rustore()
					return mod.purchaseProduct(planId)
				})
			}

			return { processed: false, entitled: false }
		},
		[provider, user.tg_user_id, user.email, runPurchase, trialEligible],
	)

	const openWebPayment = useCallback(
		(planId: PlanId) => {
			const url = buildWebPaymentUrl({
				userId: user.id,
				name: [user.luni_name, user.luni_lastname].filter(Boolean).join(" "),
				avatarUrl: user.avatar_urls.small,
				planId,
			})
			setPendingWebPayment(planId)
			openWebPaymentUrl(url)
		},
		[user.id, user.luni_name, user.luni_lastname, user.avatar_urls.small],
	)

	const syncWebPayment = useCallback(async (): Promise<PlanId | null> => {
		if (getPendingWebPayment() === null || isInProgressRef.current) return null
		isInProgressRef.current = true
		clearPendingWebPayment()
		try {
			const entitled = await syncAfterPurchase()
			if (!entitled) return null
			const { queryKey } = subscriptionStatusQueryOptions()
			const status = queryClient.getQueryData(queryKey)
			return parsePlanId(status?.tier) ?? parsePlanId(status?.plan)
		} finally {
			isInProgressRef.current = false
		}
	}, [syncAfterPurchase, queryClient])

	const restore = useCallback(async (): Promise<RestoreResult> => {
		if (
			provider !== "google-play" &&
			provider !== "app-store" &&
			provider !== "rustore"
		)
			return { active: false, failed: false }
		clearVerificationCooldown()
		try {
			const mod = await loadPlatformModule[provider]()
			const summary: { failed: boolean; storeUnauthorized?: boolean } =
				await mod.restoreTransactions()
			const status = await refreshSubscription()
			return {
				active: status?.active ?? false,
				failed: summary.failed,
				storeUnauthorized: summary.storeUnauthorized ?? false,
			}
		} catch (error) {
			console.error("Restore failed:", error)
			captureError(error, {
				scope: "purchase.restore.flow",
				store: provider,
				...purchaseErrorProps(error),
			})
			return { active: false, failed: true }
		}
	}, [provider, refreshSubscription])

	if (provider === "cloud-payments")
		return {
			provider,
			isAvailable: true,
			isLoading,
			products,
			productsError,
			trialEligible,
			isRussianStore,
			purchase,
			openWebPayment,
			syncWebPayment,
		}

	if (
		provider === "google-play" ||
		provider === "app-store" ||
		provider === "rustore"
	)
		return {
			provider,
			isAvailable: true,
			isLoading,
			products,
			productsError,
			trialEligible,
			isRussianStore,
			purchase,
			restore,
			openWebPayment,
			syncWebPayment,
		}

	return {
		provider: "cloud-payments",
		isAvailable: false,
		isLoading: false,
		products: [],
		productsError: false,
		trialEligible: false,
		isRussianStore: false,
		purchase: async () => ({ processed: false, entitled: false }),
		openWebPayment: () => {},
		syncWebPayment: async () => null,
	}
}
